/**
 * CostAttributionPanel — Tier 1.1 of the Finance module.
 *
 * Embedded as a section on any quality-event detail page (DeviationDetail,
 * CapaDetail, OosDetail, AuditFindingDetail, RecallDetail). Shows the cost
 * entries attributed to that subject, the running total, and an "Attribute
 * cost" button that opens CostEntryForm.
 *
 * Role-gated: anyone with `FinanceReader` (or any finance role) sees the
 * panel and totals. Only `FinanceEditor` / `CFO` see the attribute button.
 *
 * Plan source: coordination/FINANCE_MODULE_PLAN.md §5.4 (SPA module layout).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@edms/design-system';
import { apiFetch } from '../../../api/client';
import { useCurrentUser } from '../../../api/hooks';
import { CostEntryForm } from './CostEntryForm';
import styles from './CostAttributionPanel.module.css';

interface CostEntry {
  cost_entry_id: string;
  category_id: string;
  category_display_name: string;
  paf_bucket: string;
  total_minor_units: number | string;
  currency_code: string;
  occurred_at_utc: string;
  description: string | null;
  created_by_user_id: string;
}

interface CostEntriesResponse {
  items: CostEntry[];
  total: number;
}

interface CostAttributionPanelProps {
  /** The polymorphic subject type — one of DEVIATION / CAPA / OOS / AUDIT_FINDING / RECALL */
  subjectEntityType: string;
  /** The UUID of the subject (e.g. deviation_id) */
  subjectEntityId: string;
  /** Human label for the subject ("DEV-2026-0142 — HPLC peak shift") */
  subjectDisplayLabel?: string;
}

export function CostAttributionPanel({
  subjectEntityType,
  subjectEntityId,
  subjectDisplayLabel,
}: CostAttributionPanelProps) {
  const { t } = useTranslation();
  const userQ = useCurrentUser();
  const [formOpen, setFormOpen] = useState(false);

  const entriesQ = useQuery<CostEntriesResponse>({
    queryKey: ['finance', 'cost-entries', subjectEntityType, subjectEntityId],
    queryFn: ({ signal }) =>
      apiFetch<CostEntriesResponse>(
        `/api/v1/finance/cost-entries?subject_entity_type=${encodeURIComponent(subjectEntityType)}&subject_entity_id=${encodeURIComponent(subjectEntityId)}`,
        { signal },
      ),
    staleTime: 30_000,
  });

  // Role gating — only finance roles see the panel
  const roles = userQ.data?.roles ?? [];
  const hasFinanceRead = roles.some((r) =>
    ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'].includes(r),
  );
  const canEdit = roles.some((r) => ['FinanceEditor', 'CFO'].includes(r));

  // NOTE: this useMemo MUST run before any early return — React hooks must be
  // called unconditionally on every render (previously it sat after the
  // role/loading guards, which threw "rendered more hooks" #310 once a detail
  // screen actually loaded data and the panel proceeded past the guard).
  const total = useMemo(() => {
    if (!entriesQ.data?.items) return 0;
    return entriesQ.data.items.reduce(
      (acc, e) => acc + Number(e.total_minor_units || 0),
      0,
    );
  }, [entriesQ.data]);

  // While the user query is loading, render nothing (avoid flash)
  if (userQ.isLoading) return null;
  // If the user has no finance role, hide the whole section
  if (!hasFinanceRead) return null;

  function formatMinor(minor: number, currency = 'USD'): string {
    const fmt = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fmt.format(minor / 100);
  }

  function pafColor(bucket: string): string {
    switch (bucket) {
      case 'PREVENTION': return styles.pafPrevention;
      case 'APPRAISAL': return styles.pafAppraisal;
      case 'INTERNAL_FAILURE': return styles.pafInternal;
      case 'EXTERNAL_FAILURE': return styles.pafExternal;
      case 'LOST_OPPORTUNITY': return styles.pafLost;
      default: return '';
    }
  }

  return (
    <section className={styles.panel} aria-labelledby="cost-attribution-heading">
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 id="cost-attribution-heading" className={styles.title}>
            {t('finance.costAttribution.title', { defaultValue: 'Cost attribution' })}
          </h2>
          <span className={styles.subtitle}>
            {t('finance.costAttribution.subtitle', {
              defaultValue: 'Labor, materials and external costs incurred against this record',
            })}
          </span>
        </div>
        {canEdit && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setFormOpen(true)}
          >
            + {t('finance.costAttribution.attribute', { defaultValue: 'Attribute cost' })}
          </Button>
        )}
      </header>

      {entriesQ.isLoading && (
        <p className={styles.empty} role="status">{t('common.loading')}</p>
      )}

      {entriesQ.isError && (
        <p className={styles.empty} role="alert">
          {t('finance.costAttribution.error', { defaultValue: 'Could not load cost entries.' })}
        </p>
      )}

      {entriesQ.data?.items?.length === 0 && (
        <p className={styles.empty}>
          {t('finance.costAttribution.empty', {
            defaultValue: 'No costs attributed yet. ' +
              (canEdit ? 'Use "Attribute cost" to log labor or materials.'
                       : 'A finance user can add cost lines.'),
          })}
        </p>
      )}

      {entriesQ.data && entriesQ.data.items.length > 0 && (
        <>
          <div className={styles.totalStrip}>
            <span className={styles.totalLabel}>
              {t('finance.costAttribution.total', { defaultValue: 'Total attributed' })}
            </span>
            <span className={styles.totalAmount}>{formatMinor(total)}</span>
            <span className={styles.totalMeta}>
              {entriesQ.data.items.length}{' '}
              {entriesQ.data.items.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          <ul className={styles.entries}>
            {entriesQ.data.items.map((e) => (
              <li key={e.cost_entry_id} className={styles.entry}>
                <span className={`${styles.pafChip} ${pafColor(e.paf_bucket)}`}>
                  {e.paf_bucket.replace(/_/g, ' ')}
                </span>
                <div className={styles.entryMeta}>
                  <strong className={styles.entryCategory}>{e.category_display_name}</strong>
                  {e.description && (
                    <span className={styles.entryDesc}>{e.description}</span>
                  )}
                  <time className={styles.entryDate}>
                    {new Date(e.occurred_at_utc).toLocaleDateString()}
                  </time>
                </div>
                <span className={styles.entryAmount}>
                  {formatMinor(Number(e.total_minor_units), e.currency_code)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {formOpen && (
        <CostEntryForm
          subjectEntityType={subjectEntityType}
          subjectEntityId={subjectEntityId}
          subjectDisplayLabel={subjectDisplayLabel}
          onClose={() => setFormOpen(false)}
          onSubmitted={() => {
            setFormOpen(false);
            entriesQ.refetch();
          }}
        />
      )}
    </section>
  );
}
